'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, HelpCircle } from 'lucide-react';
import { cn } from '@/utils/cn';

const faqItems = [
  {
    question: 'Como funciona a consulta por vídeo?',
    answer:
      'Após escolher seu médico e horário, você acessa uma sala de espera virtual segura. No horário marcado, o médico inicia a chamada de vídeo com transmissão criptografada. Você precisa apenas de uma câmera e microfone no celular ou computador.',
  },
  {
    question: 'Quais médicos atendem na plataforma?',
    answer:
      'Atuamos com médicos especialistas rigorosamente verificados junto aos Conselhos Regionais de Medicina (CRM). Todos possuem registro ativo e experiência comprovada em atendimento presencial e virtual.',
  },
  {
    question: 'Como funciona o pagamento?',
    answer:
      'O pagamento é realizado de forma 100% protegida pela plataforma. Aceitamos PIX instantâneo e cartão de crédito. O valor fica retido com segurança e é liberado ao profissional somente após a realização da consulta.',
  },
  {
    question: 'A consulta tem cobertura por plano de saúde?',
    answer:
      'Atualmente operamos no modelo particular direto, garantindo agilidade e sem filas. Emitimos recibo detalhado com todos os dados fiscais e CRM para que você solicite o reembolso integral ou parcial junto ao seu plano de saúde.',
  },
  {
    question: 'Como solicito uma consulta domiciliar?',
    answer:
      'Na página inicial, escolha a opção "Consulta em casa". Informe seu endereço e selecione o especialista. Um médico credenciado próximo à sua região confirma o agendamento e vai até o seu endereço com equipamentos clínicos.',
  },
  {
    question: 'Minhas receitas, atestados e dados médicos são válidos?',
    answer:
      'Sim! As receitas, pedidos de exames e atestados são emitidos com certificado digital padrão ICP-Brasil pelo sistema Memed, aceitos em farmácias, laboratórios e empresas em todo o território nacional.',
  },
];

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-16 md:py-24 bg-muted/20 border-b border-border transition-colors">
      <div className="container-landing max-w-4xl">
        <div className="text-left mb-12">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary uppercase tracking-widest mb-2">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Tire suas dúvidas</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground tracking-tight">
            Perguntas Frequentes
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Tudo o que você precisa saber sobre o atendimento na Medikei
          </p>
        </div>

        <div className="space-y-3">
          {faqItems.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className="bg-card text-card-foreground rounded-2xl border border-border overflow-hidden transition-all shadow-xs"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full px-6 py-4 sm:py-5 flex items-center justify-between text-left hover:bg-muted/40 transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="font-bold text-sm sm:text-base text-foreground pr-4">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={cn(
                      'w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-200',
                      isOpen && 'rotate-180 text-primary'
                    )}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 text-xs sm:text-sm text-muted-foreground leading-relaxed animate-in fade-in duration-150 border-t border-border/40 pt-3">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10 pt-4">
          <p className="text-sm text-muted-foreground mb-3">Ainda tem alguma dúvida?</p>
          <Link
            to="/ajuda"
            className="inline-flex items-center gap-2 text-sm text-primary font-bold hover:text-primary-hover hover:underline transition-colors"
          >
            <span>Falar com nossa equipe de suporte</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
